import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * An ASSET IS A FILE. Nothing else.
 *
 * L50 — rarity and element belong to the COPY, not the asset. The
 * predecessors put rarity here, which made one image exactly one collectible
 * and forced a class of repair scripts when that stopped being true.
 *
 * This goes further than L50 asks. stefancoat's `AssetMetadata` flattened
 * every subtype's fields into one optional-everything bag with no
 * discriminant — `coatZones`, `patchGridSpan`, `tileMode`, `wallpaperCategory`,
 * `vinylDisplay`, `lightConfig` — so the shape told you nothing about what you
 * were holding. Everything that is *about the collectible* rather than *about
 * the file* now lives on an Item.
 *
 * What is left is what a file has: where it is, what it is, how big it is.
 */
export const AssetStatus = z.enum(['pending', 'active', 'retired']);

export const Asset = z.object({
  id: z.string(),
  channel_id: ChannelId,
  name: z.string().min(1).max(200),
  /** Tags are for FINDING things. They carry no behaviour. */
  tags: z.array(z.string().min(1).max(40)).max(40).default([]),
  /**
   * Verified by reading the bytes, never by believing the upload (L48).
   * Absent until the upload completes.
   */
  mime: z.string().nullable(),
  bytes: z.number().int().min(0).nullable(),
  width: z.number().int().min(1).nullable(),
  height: z.number().int().min(1).nullable(),
  /**
   * `pending` until the bytes have been read and accepted. A pending asset is
   * never offered anywhere: a half-finished upload must not be selectable.
   */
  status: AssetStatus,
  url: z.string().nullable(),
  created_at: z.string().datetime(),
});

/** Asking for somewhere to put a file. */
export const AssetUploadRequest = z.object({
  name: z.string().min(1).max(200),
  /**
   * What the client BELIEVES it is sending, used only to pick a storage path
   * and to fail early. The server re-derives it from the bytes and refuses a
   * mismatch — a declared mime is attacker-controlled and is never trusted.
   */
  content_type: z.string().min(1).max(100),
  bytes: z.number().int().min(1).max(25 * 1024 * 1024),
  tags: z.array(z.string().min(1).max(40)).max(40).default([]),
});

export const AssetUpload = z.object({
  asset: Asset,
  /** PUT the bytes here, then call complete. Short-lived. */
  upload_url: z.string(),
  expires_at: z.string().datetime(),
});

export const AssetPatch = z.object({
  name: z.string().min(1).max(200).optional(),
  tags: z.array(z.string().min(1).max(40)).max(40).optional(),
});

/**
 * Supply.
 *
 * L8 — `limit: null` (uncapped) and `limit: 0` (deliberately not printed) are
 * OPPOSITES. Conflating them is the difference between an infinite print and
 * an item that was never meant to exist, so they are distinct values of one
 * nullable field rather than an optional one.
 */
export const Supply = z.object({
  /** null = uncapped. 0 = never mint this. */
  limit: z.number().int().min(0).nullable(),
  issued: z.number().int().min(0).default(0),
});

/**
 * How many slots one bundle may have, and how many collectibles a pack may
 * hold in total.
 *
 * Not arbitrary. Every slot in a pack mints inside ONE transaction, and each
 * mint writes a counter, an edition claim and an instance — so a 50-item pack
 * is around 150 writes against Firestore's 500-write ceiling. Refusing it at
 * authoring time beats discovering it on the first open in front of an
 * audience.
 */
export const MAX_SLOTS = 20;
export const MAX_PACK_SIZE = 50;

/**
 * One reward a slot can draw, and how heavily.
 *
 * A slot entry names a REWARD and nothing else. Not a tag, not a tier, not a
 * nested bundle: anything a slot might want to filter on is a reward
 * definition, named here by id. Their platform let "tag" mean two different
 * things across four fields, and the only way that stops is by there being
 * one way to name a pool.
 */
export const SlotEntry = z.object({
  reward_id: z.string(),
  /**
   * A relative weight, not a percentage.
   *
   * Weights inside one slot are normalised, so 3/1 and 75/25 say the same
   * thing and neither has to add up to anything. Percentages that must total
   * 100 are a validation rule people fight; ratios are not.
   *
   * Consulted only when the slot weights explicitly.
   */
  weight: z.number().min(0).max(1_000_000).default(1),
});

/**
 * One position in the loot table.
 *
 * Five slots with different pools is how "the fifth one is a rare" gets said.
 * The per-slot model exists in stefancoat's admin and never reaches its
 * database — `build-bundle-from-box` transmits the box SIZE and drops the
 * distribution — so the preview it draws is decorative.
 */
export const BundleSlot = z.object({
  /** Shown while authoring and on the open screen. Never load-bearing. */
  label: z.string().min(1).max(60).optional(),
  /** How many draws this slot makes. */
  count: z.number().int().min(1).max(20).default(1),
  /**
   * ONE weighting mode per slot, never a mix.
   *
   * `explicit` uses the weights above. `by_remaining` ignores them and
   * weights by each reward's remaining stock, which is what makes a limited
   * reward's own configured quantities drive its odds without anyone
   * restating them.
   *
   * Mixing the two scales inside one slot is L13 exactly: unlimited entries
   * weighted 1 against limited ones weighted `remaining` (up to 500) meant
   * "never runs out" was treated as "1 in 501", and nothing surfaced the gap
   * between the odds an admin configured and the odds players got.
   */
  weighting: z.enum(['explicit', 'by_remaining']).default('explicit'),
  entries: z.array(SlotEntry).min(1).max(50),
});

export const Bundle = z.object({
  id: z.string(),
  channel_id: ChannelId,
  name: z.string().min(1).max(120),
  /**
   * The loot table, in order.
   *
   * There is no `mode` and no `items_per_bundle`. A static bundle is N slots
   * with one entry each, so there was nothing left for the flag to select;
   * pack size is the sum of the slot counts, and a stored copy of a derived
   * number is a second home for it that can disagree (L17).
   */
  slots: z.array(BundleSlot).min(1).max(MAX_SLOTS),
  supply: Supply,
  active: z.boolean().default(true),
});

export type Asset = z.infer<typeof Asset>;
export type AssetUploadRequest = z.infer<typeof AssetUploadRequest>;
export type AssetPatch = z.infer<typeof AssetPatch>;
export type Bundle = z.infer<typeof Bundle>;
export type BundleSlot = z.infer<typeof BundleSlot>;
export type SlotEntry = z.infer<typeof SlotEntry>;
export type Supply = z.infer<typeof Supply>;
